import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import i18n from 'i18next';
import type { Middleware } from 'redux';

import {
    isFetchBaseQueryError,
    isThrottleErrorResponse,
    isValidationErrorResponse,
    type ThrottleErrorResponse,
    type ValidationErrorResponse,
} from '@/shared/api';
import { isUserNotFound } from '@/shared/api/base-query-with-reauth';
import { logError } from '@/shared/lib/logging';
import { type EventDetails } from '@/shared/lib/store';

function createErrorMessage(errorEntity: FetchBaseQueryError) {
    const errorName = errorEntity.status
        ? i18n.t(`${errorEntity.status}`, { ns: 'translation' })
        : i18n.t('unknown', { ns: 'translation' });

    return `${errorName} ${i18n.t('Error', { ns: 'translation' })}`;
}

function createThrottleErrorDescription(obj: ThrottleErrorResponse): string {
    const { type, extra, message } = obj;

    return type === 'throttleType'
        ? `${i18n.t(message, { ns: 'translation' })} ${i18n.t(type, { ns: 'translation', count: +extra })}`
        : '';
}

function createValidationErrorDescription(
    obj: ValidationErrorResponse,
): string {
    const errors: string[] = [];

    Object.keys(obj).forEach((key: string) => {
        if (key === 'messages') {
            return;
        }

        const value = obj[key];

        if (!value) {
            return;
        }

        if (Array.isArray(value)) {
            errors.push(
                ...value.map((v: string) => i18n.t(v, { ns: 'translation' })),
            );
        } else {
            errors.push(i18n.t(value, { ns: 'translation' }));
        }
    });

    return errors.join('. ');
}

function createEventDetails(errorEntity: unknown): EventDetails {
    if (isFetchBaseQueryError(errorEntity)) {
        if (isUserNotFound(errorEntity)) {
            return {
                message: i18n.t('401', { ns: 'translation' }),
                description: i18n.t('userNotFound', { ns: 'translation' }),
            };
        }

        if (isThrottleErrorResponse(errorEntity.data)) {
            return {
                message: createErrorMessage(errorEntity),
                description: createThrottleErrorDescription(errorEntity.data),
            };
        }

        if (isValidationErrorResponse(errorEntity.data)) {
            return {
                message: createErrorMessage(errorEntity),
                description: createValidationErrorDescription(errorEntity.data),
            };
        }

        if (typeof errorEntity.status === 'string') {
            return {
                message: i18n.t(errorEntity.status, { ns: 'translation' }),
                description: i18n.t(errorEntity.error, { ns: 'translation' }),
            };
        }
    }

    return {
        message: i18n.t('unknownError', { ns: 'translation' }),
        description: i18n.t('somethingWentWrong', { ns: 'translation' }),
    };
}

export const ErrorLogger: Middleware =
    () => (next: (action: unknown) => unknown) => (action: unknown) => {
        if (isRejectedWithValue(action)) {
            // Log errors to console (automatically sent to Sentry in production)
            const errorDetails = createEventDetails(action.payload);

            // eslint-disable-next-line no-console
            logError('API Error:', {
                message: errorDetails.message,
                description: errorDetails.description,
                payload: action.payload,
            });
        }

        return next(action);
    };
